import React, { useState, useEffect, useMemo } from 'react';
import {
  Calendar, Users, DollarSign, Clock, CheckCircle, AlertCircle,
  User, ChevronRight, TrendingUp, Activity, MessageCircle, Star, Crown,
  Zap, Award, Flame
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion'; // npm i framer-motion
import { useSupabase } from '../context/SupabaseContext';
import { UserRole } from '../components/interfaces/All';
import {calculateDebtPerStudent} from './Wallet.tsx'

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const {state, fetchNotifications, fetchUserRole, fetchMyLedger,fetchStudents} = useSupabase();
  const [streakCount, setStreakCount] = useState(7); // Gamification!
  const [level, setLevel] = useState(12);
  const [dailyGoal, setDailyGoal] = useState(75); // 75% to goal

  // const [userRole, setUserRole] = useState<UserRole>('student');
  const lessonsToday = state.lessons.filter(l => new Date(l.time).setHours(0, 0, 0, 0) == new Date().setHours(0, 0, 0, 0)).length
  const nextLesson = state.lessons.filter(l=>new Date(l.time) > new Date())[0] || [];
  const ledgerPaid = state.ledger?.filter(p=>new Date(p.date).getMonth === new Date().getMonth).map(p => p.amount) ?? [];
  const totalPaid = ledgerPaid.reduce((sum, amount) => sum + amount, 0);
  const totalActiveStudents = Array.isArray(state.students)
    ? state.students.filter((s) => s.status === 'active').length
    : 0;

  // student stats
  const pricePerH = 10; // TODO pricePerH
  // const priceForLessons  = state.lessons.filter(l=>l.student_id == state.userProfile.id).length * pricePerH;

  // TODO - handle this better (teacher ok, but nmissing for student and parent)
  let owedMoney = {students: [state.userProfile], lessons:[], ledger:[]};
  if(state.userRole == 'teacher') {
    owedMoney =  calculateDebtPerStudent([state.userProfile], state.lessons.filter(l=>l.student_id==state.userProfile.id), state.ledger); //priceForLessons - totalPaid - state.userProfile.balance;
  }
  const passedLessons = state.lessons.filter(l => new Date(l.time) < new Date())
  const averageGrade = passedLessons.map(l=>l.teacher_rating).reduce((sum,amount)=>sum+amount,0)/ passedLessons.length

  // admin
  const totalActiveTeachers = state.teachers.filter(s=>s.status=='active').length

  const userRole = state.userRole;
  const usercontent = {
    teacher: {
      title: "Dashboard",
      feedItems: [
        { icon: Calendar, title: `${lessonsToday} Lessons Today`, subtitle: "See your lessons", path: '/lesson' },
        { icon: DollarSign, title: `€${totalPaid} This Month`, subtitle: "List of payments", path: '/ledger' },
        { icon: Users, title: `${totalActiveStudents} Active Students`, subtitle: 'All students', path: '/students' }
      ]
    },
    student:{
      title: "Your Journey ✨",
      feedItems: [
        { icon: Clock, title: `Next: ${nextLesson.time?.slice(0,9)} • ${nextLesson.time?.slice(10,15)}`, subtitle: ``, path: '/lesson' },
        { icon: DollarSign, title: `€${owedMoney} Due`, subtitle: "Payment reminder", path: '/ledger' },
        { icon: Star, title: `${averageGrade}★ Average`, subtitle: "You're crushing it! 🔥", path: '/profile' }
      ]
    },
    admin: {
      title: "Your Journey ✨",
      feedItems: [
        { icon: Calendar, title: `${lessonsToday} Lessons Today`, subtitle: "", path: '/lesson' },
        { icon: Users, title: `${totalActiveTeachers} Active Teachers`, subtitle: '', path: '/teachers' }
      ]
    }
  };
  const content = usercontent[ 'teacher' ];

  useMemo(()=>{
    if (state.isSyncing == true) return
    // redirect to onboarding if auth profile not connected with account profile
    if (userRole == undefined || !state.userProfile.auth_id || state.userProfile.auth_id == "") {
      navigate('/onboarding');
    }
  },[state.isSyncing])
  // useEffect(() => {
  // }, [fetchNotifications, fetchUserRole]);

  const notifications = state.notifications || [];

  return (
    <div className="fade-in max-w-md mx-auto pb-20 min-h-screen bg-gradient-to-b">

      {/* 1️⃣ STORIES FEED (Instagram-style top bar) */}

      <div className="flex gap-2 pt-2 overflow-x-auto  ">
        <AnimatePresence>
        {notifications.map((story, i) => {
          const isUnseen = !story.seen;
          return (
            <motion.div
              key={story.id}
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.6, opacity: 0 }}
              transition={{ delay: i * 0.1 }}
              className="relative flex flex-col items-center group cursor-pointer"
              onClick={() => {
                navigate(story.destination);
              }}
            >
              {/* MAIN CIRCLE */}
              <div className={`
                w-16 h-16 rounded-3xl flex items-center justify-center shadow-sm border-4 border-white/50
                transition-all duration-300 hover:scale-105 hover:shadow-lg
                ${story.type === 'success'
                  ? 'bg-gradient-to-br from-emerald-400/20 to-emerald-500/20'
                  : story.type === 'money'
                  ? 'bg-gradient-to-br from-amber-400/20 to-amber-500/20'
                  : 'bg-gradient-to-br from-blue-400/20 to-blue-500/20'
                }
                ${isUnseen ? 'backdrop-blur-sm' : 'backdrop-blur-none'}
              `}>
                {/* Inner Icon */}
                <div className={`w-10 h-10 rounded-2xl flex items-center justify-center shadow-lg border border-white/60 ${isUnseen ? 'bg-white/70' : 'bg-white'}`}>
                  {story.type === 'success' && <Star className="w-5 h-5 text-emerald-600" />}
                  {story.type === 'money' && <DollarSign className="w-5 h-5 text-amber-600" />}
                  {story.type === 'urgent' && <Activity className="w-5 h-5 text-blue-600" />}
                </div>
              </div>

              {/* SHORT LABEL */}
              <p className="mt-2 text-xs font-semibold text-center leading-tight max-w-[60px] truncate">
                {story.shortText || story.text}
              </p>

              {/* UNSEEN DOT */}
              {isUnseen && (
                <motion.div
                  className="absolute -top-1 -right-1 w-3 h-3 bg-red-400 rounded-full border-2 border-white shadow-lg"
                  animate={{ scale: [1, 1.15, 1] }}
                  transition={{ duration: 1.2, repeat: Infinity }}
                />
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>
      </div>

      {/* 2️⃣ GAMIFICATION HEADER */}
      <div className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black  bg-clip-text ">
              {content.title}
            </h1>
            <p className="text-sm mt-1"></p>
          </div>
        </div>

        {/* 🔥 STREAK + PROGRESS */}
        {/* <div className="grid grid-cols-3 gap-3">
          <div className="card p-4 text-center group">
            <Flame className={`w-8 h-8 mx-auto mb-2 ${streakCount > 0 ? 'text-orange-500 animate-pulse' : 'text-gray-400'}`} />
            <div className="text-2xl font-black ">{streakCount}</div>
            <p className="text-xs  uppercase tracking-wider">Day Streak</p>
          </div>

          <div className="card p-4 text-center relative">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs  uppercase tracking-wider font-medium">Daily Goal</span>
              <Crown className="w-4 h-4 text-yellow-500" />
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-emerald-500 to-blue-500 h-2 rounded-full transition-all duration-1000"
                style={{ width: `${dailyGoal}%` }}
              />
            </div>
            <p className="text-xs font-bold text-emerald-600 mt-1">{dailyGoal}%</p>
          </div>

          <div className="card p-4 text-center">
            <TrendingUp className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
            <div className="text-lg font-black">+12%</div>
            <p className="text-xs uppercase tracking-wider">This Week</p>
          </div>
        </div> */}
      </div>

      {/* 3️⃣ FEED (Primary Content - replaces cards) */}
      <div className="space-y-4">
        {content.feedItems.map((item, i) => (
          <motion.div
            key={item.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="card group p-6 relative overflow-hidden bg-gradient-to-r from-white/70 to-gray-50/70 backdrop-blur-sm hover:shadow-2xl transition-all border-0 hover:border-emerald-200/50"
            onClick={() => navigate(item.path)}
          >
            <div className={`absolute top-4 right-4 w-16 h-16 bg-gradient-to-br ${
              userRole === 'teacher'
                ? 'from-emerald-400/20 via-emerald-500/20 to-blue-500/20'
                : 'from-blue-400/20 via-purple-500/20 to-pink-500/20'
            } rounded-2xl flex items-center justify-center backdrop-blur-sm`}>
              <item.icon className={`w-8 h-8 ${userRole === 'teacher' ? 'text-emerald-600' : 'text-blue-600'}`} />
            </div>

            <div className="relative z-10">
              <h3 className="text-xl font-black text-gray-900 mb-1">{item.title}</h3>
              <p className="text-sm text-gray-600 mb-4">{item.subtitle}</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full font-semibold">
                  <Zap className="w-3 h-3" />
                  Quick Action
                </div>
                <ChevronRight className="w-6 h-6 text-gray-400 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

    </div>
  );
};

export default Dashboard;
